import React from 'react';
import PropTypes from 'prop-types';

// Human-facing label + icon per comment event (#389). Kept as data so the
// timeline renders comment events distinctly from version edits.
const COMMENT_EVENT_META = {
  comment_added: { icon: '💬', label: 'Comment added' },
  comment_replied: { icon: '↳', label: 'Comment reply' },
  comment_resolved: { icon: '✓', label: 'Comment resolved' },
  comment_wont_fix: { icon: '⊘', label: "Comment marked won't fix" },
  comment_closed: { icon: '🗕', label: 'Comment closed' },
};

// #520 — AI-review and per-reviewer approval events, same discriminated-event
// pattern as comments (#389).
const AI_REVIEW_EVENT_META = {
  ai_review_completed: { icon: '🤖', label: 'AI review' },
};

const APPROVAL_EVENT_META = {
  approved: { icon: '✅', label: 'Approved' },
  not_approved: { icon: '⛔', label: 'Not approved' },
  // F-50 / REQ-6111 (#590): `cleared` is one of the three recorded transitions
  // (core.requirements.feedback.APPROVAL_EVENTS) and had NO entry here, so the
  // `|| APPROVAL_EVENT_META.approved` fallback below rendered a WITHDRAWN decision as
  // "✅ Approved". "Approved, then withdrawn" is the single question an approval audit
  // exists to answer, and the timeline was answering it backwards.
  cleared: { icon: '↩️', label: 'Decision withdrawn' },
  // F-60 / REQ-6118 (#606, #629): a PRIVILEGED actor clearing ANOTHER reviewer's
  // decision — distinct from `cleared` (the reviewer withdrawing their OWN decision).
  // Reusing `cleared`'s "Decision withdrawn" here would misattribute the action to the
  // person it was done TO, exactly the failure mode #606's D-W4b-4 rejected. Without an
  // entry this fell through to `|| APPROVAL_EVENT_META.approved`, i.e. an override on a
  // REJECTION would have rendered as "✅ Approved" — worse than merely mislabelled.
  overridden: { icon: '⚖️', label: 'Approval overridden' },
  // The fallback below USED to be `|| APPROVAL_EVENT_META.approved`, and the two
  // comments above are the record of that causing this exact bug twice: `cleared`
  // rendered as "✅ Approved", then an `overridden` on a REJECTION rendered as
  // "✅ Approved". Both times the fix was to add the missing entry and leave the
  // trap armed for whoever adds the next transition. An unrecognised governance
  // event must not resolve to the most favourable reading available — in an audit
  // timeline that is the one direction a wrong guess must never go. Unknown events
  // now fall through to this, which claims nothing.
  unknown: { icon: '❔', label: 'Unrecognised approval event' },
};

// F-50 / REQ-6111 (#590): the trail records what each transition moved AWAY from, so a
// withdrawal can name what was withdrawn rather than leaving the reader to infer it.
const PREV_STATE_LABEL = {
  approved: 'withdrew their approval',
  not_approved: 'withdrew their rejection',
};

// Per-dimension AI-review scores as "clarity: 4, completeness: 3, ...".
function formatScores(scores) {
  if (!scores || typeof scores !== 'object') return '';
  return Object.entries(scores).map(([dim, val]) => `${dim}: ${val}`).join(', ');
}

// Version history + comment/AI-review/approval events as the agreed mockup's
// timeline (history.html / ds.css .tl): newest first. Version nodes carry a
// version number + compare/restore actions; comment/AI-review/approval-event
// nodes (#389/#520) render with a distinct icon/label and no version actions
// (comment events additionally link to the Comments tab). A sibling reorder
// (#520) is an ORDINARY version row (change_type:'reorder' — a genuine
// version-chain mutation, not a separate event stream) that gets a small
// "Reordered" badge alongside its version number so it still reads as
// distinct in the timeline.
// Behaviour + a11y labels for version rows are unchanged (HistoryTab.test.js pins them).
const HistoryTab = ({ history, handleCompareVersion, handleRestoreVersion, onOpenComment }) => {
  if (history.length === 0) {
    return <div className="t-body muted3" style={{ marginTop: 8 }}>No history available for this item.</div>;
  }
  // Only the newest VERSION row is "current" — a comment event newer than it
  // must not be mistaken for the current version (which drives restore gating).
  const firstVersionIndex = history.findIndex((h) => (h.entryType ?? 'version') === 'version');
  return (
    <div className="tl" style={{ marginTop: 10 }}>
      {history.map((hist, index) => {
        if (hist.entryType === 'comment_event') {
          const meta = COMMENT_EVENT_META[hist.event] || COMMENT_EVENT_META.comment_added;
          const detail = hist.event === 'comment_resolved' || hist.event === 'comment_wont_fix'
            ? hist.resolution
            : hist.body;
          return (
            <div className="tlitem" key={`c-${hist.commentId || index}-${hist.event}`}>
              <span className="tldot muted" />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="t-strong" style={{ fontSize: 12.5 }} aria-hidden="true">{meta.icon}</span>
                <span className="t-strong" style={{ fontSize: 12.5 }}>{meta.label}</span>
                <div style={{ flex: 1 }} />
                {onOpenComment && hist.commentId && (
                  <button type="button"
                    className="btn ghost sm"
                    aria-label={`View comment (${meta.label})`}
                    title="Open this comment in the Comments tab"
                    onClick={() => onOpenComment(hist.commentId)}
                  >
                    View comment
                  </button>
                )}
              </div>
              <div className="t-body muted3" style={{ fontSize: 11 }}>
                <span>{hist.date}</span>{hist.author ? <> · <span>{hist.author}</span></> : ''}
              </div>
              {detail && (
                <div className="t-body" style={{ fontSize: 12.5, marginTop: 2 }}>{detail}</div>
              )}
            </div>
          );
        }
        if (hist.entryType === 'ai_review_event') {
          const meta = AI_REVIEW_EVENT_META[hist.event] || AI_REVIEW_EVENT_META.ai_review_completed;
          const scoresText = formatScores(hist.scores);
          return (
            <div className="tlitem" key={`r-${hist.timestamp || index}-${hist.event}`}>
              <span className="tldot muted" />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="t-strong" style={{ fontSize: 12.5 }} aria-hidden="true">{meta.icon}</span>
                <span className="t-strong" style={{ fontSize: 12.5 }}>{meta.label}</span>
                {hist.model && <span className="badge">{hist.model}</span>}
              </div>
              <div className="t-body muted3" style={{ fontSize: 11 }}>
                <span>{hist.date}</span>{hist.author ? <> · <span>{hist.author}</span></> : ''}
              </div>
              {scoresText && (
                <div className="t-body" style={{ fontSize: 12.5, marginTop: 2 }}>{scoresText}</div>
              )}
            </div>
          );
        }
        if (hist.entryType === 'approval_event') {
          const meta = APPROVAL_EVENT_META[hist.event] || APPROVAL_EVENT_META.unknown;
          const withdrew = hist.event === 'cleared' && PREV_STATE_LABEL[hist.prevState];
          // F-60 / REQ-6118 (#606, #629): name BOTH parties — who overrode whom. `author`
          // is the overriding actor (backend sets author = actor for this event); `reviewer`
          // is the party overruled. Must not read like `withdrew` above, which would say
          // the OVERRULED reviewer changed their own mind.
          const overrodeLine = hist.event === 'overridden'
            ? `overrode ${hist.reviewer || 'a reviewer'}’s ${hist.prevState === 'not_approved' ? 'rejection' : 'approval'}`
            : null;
          // F-12 / REQ-6102 (#592, D-05): a decision that reads fresh only because a
          // restore brought the signed-off content back must say so, not pass as a
          // sign-off against the current revision.
          const drift = !hist.stale && hist.versionDrift && hist.approvedAtVersion != null;
          return (
            <div className="tlitem" key={`a-${hist.timestamp || index}-${hist.event}`} data-testid={`history-approval-${index}`}>
              <span className="tldot muted" />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span className="t-strong" style={{ fontSize: 12.5 }} aria-hidden="true">{meta.icon}</span>
                <span className="t-strong" style={{ fontSize: 12.5 }}>{meta.label}</span>
                {/* F-52 / REQ-6113 (#591, D-04): a self-issued sign-off must be visible in
                    the TIMELINE, not only on the live Breakdown row — the event carries
                    the marker, so it stays true after the decision is cleared. */}
                {hist.selfApproved && (
                  <span
                    className="badge warning"
                    data-testid={`history-self-approved-${index}`}
                    title="This reviewer is the item's author — a self-issued sign-off"
                  >
                    self-approved
                  </span>
                )}
                {hist.stale && (
                  <span className="badge muted" title="Item changed after this decision was recorded">stale</span>
                )}
                {drift && (
                  <span
                    className="badge neutral"
                    data-testid={`history-version-drift-${index}`}
                    title="Issued against an earlier revision; the signed-off content was later restored"
                  >
                    {`at v${hist.approvedAtVersion}`}
                  </span>
                )}
                {/* F-09 / REQ-6099 (#603, D-08): name the party overruled. */}
                {hist.supersededReviewers && hist.supersededReviewers.length > 0 && (
                  <span
                    className="badge accent"
                    data-testid={`history-superseded-${index}`}
                    title="This approval superseded a live rejection, which is retained on the record"
                  >
                    {`over ${hist.supersededReviewers.join(', ')}`}
                  </span>
                )}
              </div>
              <div className="t-body muted3" style={{ fontSize: 11 }}>
                <span>{hist.date}</span>{hist.author ? <> · <span>{hist.author}</span></> : ''}
                {withdrew ? <> · <span>{withdrew}</span></> : ''}
                {overrodeLine ? <> · <span data-testid={`history-override-${index}`}>{overrodeLine}</span></> : ''}
              </div>
              {/* F-06 / REQ-6096 (#603, D-09): the rejection rationale is retained AND
                  displayed — an auditor reading the trail must see why, not only that.
                  Plain text, like every other reviewer-supplied string in this app. */}
              {hist.reason && (
                <div className="t-body" style={{ fontSize: 12, marginTop: 2, whiteSpace: 'pre-wrap' }}
                     data-testid={`history-reason-${index}`}>
                  <span className="muted3">Reason:</span> {hist.reason}
                </div>
              )}
              {/* F-08 / REQ-6098 (#603, D-08). */}
              {hist.justification && (
                <div className="t-body" style={{ fontSize: 12, marginTop: 2, whiteSpace: 'pre-wrap' }}
                     data-testid={`history-justification-${index}`}>
                  <span className="muted3">Justification:</span> {hist.justification}
                </div>
              )}
            </div>
          );
        }
        // The newest version row is the current version — restoring it is a
        // no-op, so only offer restore on older versions.
        const isCurrent = index === firstVersionIndex;
        return (
          <div className="tlitem" key={`v-${hist.version ?? index}`}>
            <span className={`tldot${isCurrent ? '' : ' muted'}`} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="t-strong" style={{ fontSize: 12.5 }}>Version {hist.version}</span>
              {isCurrent && <span className="badge accent">current</span>}
              {hist.changeType === 'reorder' && (
                <span className="badge" title="Sibling order changed" aria-label="Reordered">↕ Reordered</span>
              )}
              <div style={{ flex: 1 }} />
              <button type="button"
                className="btn ghost sm"
                aria-label={`Compare version ${hist.version}`}
                title="Compare this version with current"
                onClick={() => handleCompareVersion(hist.version)}
              >
                Compare
              </button>
              {handleRestoreVersion && !isCurrent && (
                <button type="button"
                  className="btn ghost sm"
                  aria-label={`Restore version ${hist.version}`}
                  title="Restore this version"
                  onClick={() => handleRestoreVersion(hist.version)}
                >
                  Restore
                </button>
              )}
            </div>
            <div className="t-body muted3" style={{ fontSize: 11 }}>
              <span>{hist.date}</span>{hist.author ? <> · <span>{hist.author}</span></> : ''}
            </div>
            <div className="t-body" style={{ fontSize: 12.5, marginTop: 2 }}>{hist.changes}</div>
            {hist.reason && (
              <div className="t-body muted3" style={{ fontSize: 11 }}>Reason: {hist.reason}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

HistoryTab.propTypes = {
  history: PropTypes.array.isRequired,
  handleCompareVersion: PropTypes.func.isRequired,
  handleRestoreVersion: PropTypes.func,
  onOpenComment: PropTypes.func,
};

export default HistoryTab;
